/** כמה ימים לפני תחילה מציגים מבצע חודשי עתידי למשתמשים */
export const MONTHLY_FUTURE_VISIBLE_DAYS = 7;

function startOfDay(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

/** מבצע חודשי עתידי — רק מ־7 ימים לפני תאריך ההתחלה */
export function isMonthlyFutureDueForUsers(promotion, now = new Date()) {
  const duration = promotion?.duration || promotion?.type;
  if (duration !== "monthly") return true;
  const start = startOfDay(promotion?.start_date);
  if (!start) return true;
  const today = startOfDay(now);
  if (!today) return true;
  const daysUntilStart = Math.round((start.getTime() - today.getTime()) / 86400000);
  return daysUntilStart <= MONTHLY_FUTURE_VISIBLE_DAYS;
}

/** מצב מבצע לפי תאריכים: נוכחי / עתידי / פג תוקף */
export function getPromotionTimeStatus(promotion, now = new Date()) {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const start = promotion?.start_date ? new Date(promotion.start_date) : null;
  const end = promotion?.end_date ? new Date(promotion.end_date) : null;

  if (start) start.setHours(0, 0, 0, 0);
  if (end) end.setHours(23, 59, 59, 999);

  if (end && end < today) return "expired";
  if (start && start > today) return "future";
  return "current";
}

/** מבצע שניתן לשלוח לסניפים — נוכחי או עתידי (לא פג תוקף) */
export function isPromotionSendable(promotion, now = new Date()) {
  if (!promotion || promotion.is_active === false) return false;
  const status = getPromotionTimeStatus(promotion, now);
  return status === "current" || status === "future";
}

export const PROMOTION_TIME_STATUS_LABELS = {
  current: "מבצע נוכחי",
  future: "מבצע עתידי",
  expired: "פג תוקף",
};

export const PROMOTION_DURATION_LABELS = {
  weekly: "מבצע שבועי",
  monthly: "מבצע חודשי",
  thursday_friday: "חמישי שישי",
};

export const PROMOTION_CATEGORY_LABELS = {
  regular: "מבצע רגיל",
  club: "מבצע מועדון",
  distribution: "חלוקה",
};

const PROMOTION_DURATION_BADGE_CLASS = {
  weekly: "bg-orange-500 text-white border-0",
  monthly: "bg-purple-500 text-white border-0",
  thursday_friday: "bg-rose-500 text-white border-0",
};

const PROMOTION_CATEGORY_BADGE_CLASS = {
  club: "border-indigo-600 text-indigo-700 bg-indigo-50",
  distribution: "border-blue-600 text-blue-700 bg-blue-50",
};

const PROMOTION_TIME_BADGE_CLASS = {
  current: "bg-green-500 text-white border-0",
  future: "bg-blue-500 text-white border-0",
};

/** תוויות להצגה על כרטיס מוצר במסך מבצעים */
export function getPromotionDisplayBadges(promotion) {
  if (!promotion) return [];

  const badges = [];
  const duration = promotion.duration || promotion.type;

  if (duration && PROMOTION_DURATION_LABELS[duration]) {
    badges.push({
      key: `duration-${duration}`,
      label: PROMOTION_DURATION_LABELS[duration],
      className: PROMOTION_DURATION_BADGE_CLASS[duration] || "bg-gray-500 text-white border-0",
    });
  }

  const category = promotion.category;
  if (category && category !== "regular" && PROMOTION_CATEGORY_LABELS[category]) {
    badges.push({
      key: `category-${category}`,
      label: PROMOTION_CATEGORY_LABELS[category],
      variant: "outline",
      className: PROMOTION_CATEGORY_BADGE_CLASS[category] || "border-gray-400 text-gray-700 bg-gray-50",
    });
  }

  const timeStatus = getPromotionTimeStatus(promotion);
  if (timeStatus === "current" || timeStatus === "future") {
    badges.push({
      key: `time-${timeStatus}`,
      label: PROMOTION_TIME_STATUS_LABELS[timeStatus],
      className: PROMOTION_TIME_BADGE_CLASS[timeStatus],
    });
  }

  return badges;
}

function formatHeDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("he-IL");
}

export function formatPromotionDateRange(promotion) {
  const start = formatHeDate(promotion?.start_date);
  const end = formatHeDate(promotion?.end_date);
  if (start && end) return `${start}–${end}`;
  return start || end || "";
}

function promotionDayKey(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-CA", { timeZone: "Asia/Jerusalem" });
}

function normalizeDealText(value) {
  return String(value || "").trim().replace(/\s+/g, " ");
}

/** עיצוב מחיר בשקלים — null אם אין ערך תקין */
export function formatShekelPrice(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  const hasFraction = Math.abs(num - Math.round(num)) > 0.001;
  const text = hasFraction ? num.toFixed(2) : String(Math.round(num));
  return `₪${text}`;
}

/** מחיר רגיל של המוצר (שדה price בקטלוג) — לא מחיר מבצע */
export function resolveProductRegularPrice(product, promotionMeta = null) {
  const candidates = [
    product?.price,
    product?.regular_price,
    promotionMeta?.regularPrice,
    promotionMeta?.productPrice,
  ];
  for (const value of candidates) {
    const num = Number(value);
    if (Number.isFinite(num) && num > 0) return num;
  }
  return null;
}

/** מחיר ליחידה לפני המבצע — המחיר הרגיל של המוצר */
export function getPromotionOriginalUnitPrice(product, promotionMeta = null) {
  return formatShekelPrice(resolveProductRegularPrice(product, promotionMeta));
}

/** הוספת ₪ למחיר בטקסט מבצע — למשל «2 ב-11.9» → «2 ב-₪11.9» */
export function formatPromotionDealTextWithShekel(dealText) {
  const text = normalizeDealText(dealText);
  if (!text) return "";
  if (text.includes("₪")) return text;

  const multiBuy = text.match(/^(\d+)\s*ב\s*-?\s*(\d+(?:\.\d+)?)$/i);
  if (multiBuy) {
    return `${multiBuy[1]} ב-₪${multiBuy[2]}`;
  }

  const singlePrice = text.match(/^(\d+(?:\.\d+)?)$/);
  if (singlePrice) {
    return formatShekelPrice(singlePrice[1]) || text;
  }

  return text.replace(
    /(\d+)\s*ב\s*-?\s*(\d+(?:\.\d+)?)/gi,
    (_, qty, price) => `${qty} ב-₪${price}`
  );
}

/** חילוץ טקסט המבצע בלבד מתיאור מלא (ללא שם מוצר), למשל «2 ב-10» */
export function extractPromotionDealText(description, productName = "") {
  let text = normalizeDealText(description);
  if (!text) return "";

  const name = normalizeDealText(productName);
  if (name) {
    const lowerText = text.toLowerCase();
    const lowerName = name.toLowerCase();
    if (lowerText.startsWith(lowerName)) {
      text = normalizeDealText(text.slice(name.length));
      text = text.replace(/^[-–—]\s*/, "");
    }
  }

  const separators = [" - ", " – ", " — ", "- ", " -", "– ", "— "];
  for (const sep of separators) {
    const idx = text.indexOf(sep);
    if (idx >= 0) {
      const tail = normalizeDealText(text.slice(idx + sep.length));
      if (tail && /\d/.test(tail)) return tail;
    }
  }

  const glued = text.match(/[-–—]\s*(\d+(?:\s*ב\s*-?\s*\d+(?:\.\d+)?|\s*ב\s*\d+(?:\.\d+)?)?)\s*$/);
  if (glued?.[1]) return normalizeDealText(glued[1]);

  const dealOnly = text.match(/(\d+\s*ב\s*-?\s*\d+(?:\.\d+)?|\d+\s*ב\s*\d+(?:\.\d+)?)\s*$/);
  if (dealOnly?.[1]) return normalizeDealText(dealOnly[1]);

  const trailingNum = text.match(/(\d+)\s*$/);
  if (trailingNum?.[1] && text.length > trailingNum[1].length + 3) {
    return trailingNum[1];
  }

  return text;
}

/** מפתח לזהות מבצע זהה על אותו מוצר (ייבוא כפול) */
export function promotionDedupeKey(promo) {
  const pid = normalizePromotionProductId(promo?.product_id);
  const reward = String(promo?.reward_code || "").trim();
  const start = promotionDayKey(promo?.start_date);
  const end = promotionDayKey(promo?.end_date);
  if (pid && reward && start && end) return `rw:${pid}|${reward}|${start}|${end}`;
  const desc = normalizeDealText(promo?.description);
  return `ds:${pid}|${desc}|${start}|${end}`;
}

/** משאיר עותק אחד לכל מבצע זהה — הראשון ברשימה */
export function dedupePromotionsForDisplay(promotions = []) {
  const seen = new Set();
  const out = [];
  for (const promo of promotions || []) {
    if (!promo) continue;
    const key = promotionDedupeKey(promo);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(promo);
  }
  return out;
}

export function getVisiblePromotions(promotions = []) {
  return dedupePromotionsForDisplay((promotions || []).filter((p) => isPromotionVisible(p)));
}

/** שורות תצוגה למנהל סניף: כל מבצע חי בנפרד, נוכחי ואז עתידי */
export function getPromotionDealLines(promotions = []) {
  const visible = getVisiblePromotions(promotions);
  const ranked = [...visible].sort((a, b) => {
    const ta = getPromotionTimeStatus(a);
    const tb = getPromotionTimeStatus(b);
    if (ta !== tb) return ta === "current" ? -1 : 1;
    return new Date(a.start_date || 0) - new Date(b.start_date || 0);
  });
  return ranked.map((promo, index) => {
    const timeStatus = getPromotionTimeStatus(promo);
    const duration = promo.duration || promo.type;
    const dates = formatPromotionDateRange(promo);
    const description = String(promo.description || "").trim();
    return {
      key: String(promo._id || promo.id || `${promo.reward_code || ""}-${dates}-${index}`),
      timeStatus,
      duration,
      durationLabel: PROMOTION_DURATION_LABELS[duration] || "",
      timeLabel: PROMOTION_TIME_STATUS_LABELS[timeStatus] || "",
      dates,
      description,
      isClub: promo.category === "club",
    };
  });
}

/** מיזוג תוויות מכמה מבצעים על אותו מוצר — ללא כפילויות */
export function getPromotionDisplayBadgesFromList(promotions = []) {
  const byKey = new Map();
  for (const promo of promotions) {
    for (const badge of getPromotionDisplayBadges(promo)) {
      if (!byKey.has(badge.key)) byKey.set(badge.key, badge);
    }
  }
  return Array.from(byKey.values());
}

export function normalizePromotionProductId(productId) {
  if (!productId) return "";
  if (typeof productId === "object") return String(productId._id || productId.id || "");
  return String(productId);
}

export function isPromotionVisible(promotion, now = new Date()) {
  if (!promotion) return false;
  if (promotion.is_active === false) return false;
  const status = getPromotionTimeStatus(promotion, now);
  if (status === "expired") return false;
  if (status === "future" && !isMonthlyFutureDueForUsers(promotion, now)) return false;
  return true;
}

function idStr(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object") return String(value._id ?? value.id ?? "").trim();
  return String(value).trim();
}

/**
 * אם יש רשתות — רק הן. אם יש סניפים — רק הם.
 * רשת + סניף: חייבים להתאים לשניהם (סניף של רשת אחרת לא רואה את המבצע).
 */
export function promotionAppliesToStore(promo, storeId, chainId) {
  const pStoreIds = (promo?.store_ids || []).map(idStr).filter(Boolean);
  const pChainIds = (promo?.chain_ids || []).map(idStr).filter(Boolean);
  if (pStoreIds.length === 0 && pChainIds.length === 0) return true;

  const sid = idStr(storeId);
  const cid = idStr(chainId);

  if (pChainIds.length > 0 && cid && !pChainIds.includes(cid)) return false;
  if (pStoreIds.length > 0 && sid && !pStoreIds.includes(sid)) return false;
  if (pChainIds.length > 0 && !cid && pStoreIds.length === 0) return false;
  if (pStoreIds.length > 0 && !sid && pChainIds.length === 0) return false;
  return true;
}

/** אינדקס מבצעים פעילים לפי מזהה מוצר. עם סניף/רשת — רק מבצעים שחלים עליהם */
export function buildPromotionsByProductId(promotions = [], storeId, chainId) {
  const scoped = storeId || chainId;
  const map = new Map();
  for (const promo of promotions) {
    if (!isPromotionVisible(promo)) continue;
    if (scoped && !promotionAppliesToStore(promo, storeId, chainId)) continue;
    const pid = normalizePromotionProductId(promo.product_id);
    if (!pid) continue;
    if (!map.has(pid)) map.set(pid, []);
    map.get(pid).push(promo);
  }
  for (const [pid, list] of map) {
    map.set(pid, dedupePromotionsForDisplay(list));
  }
  return map;
}

export function getPromotionsForProduct(productId, promotions = [], storeId, chainId) {
  const pid = normalizePromotionProductId(productId);
  const scoped = storeId || chainId;
  return dedupePromotionsForDisplay(
    promotions.filter((p) => {
      if (!isPromotionVisible(p)) return false;
      if (normalizePromotionProductId(p.product_id) !== pid) return false;
      if (scoped && !promotionAppliesToStore(p, storeId, chainId)) return false;
      return true;
    })
  );
}

function getOrderDate(order) {
  return new Date(order?.createdAt || order?.created_date || order?.created_at || 0);
}

/** מזהי מוצרים במבצע נוכחי (פעיל, בתוקף) */
export function getCurrentPromotionProductIds(promotions = [], now = new Date()) {
  const ids = new Set();
  for (const promo of promotions) {
    if (promo.is_active === false) continue;
    if (getPromotionTimeStatus(promo, now) !== "current") continue;
    const pid = normalizePromotionProductId(promo.product_id);
    if (pid) ids.add(pid);
  }
  return [...ids];
}

/** מוצרים במבצע נוכחי שלא הוזמנו ב-{days} הימים האחרונים */
export function getUnorderedPromotionProductIds(
  promotions = [],
  orders = [],
  { days = 7, now = new Date() } = {}
) {
  const since = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  const promoProductIds = getCurrentPromotionProductIds(promotions, now);

  const orderedProductIds = new Set();
  for (const order of orders) {
    if (getOrderDate(order) < since) continue;
    if (order.status === "cancelled") continue;
    for (const item of order.items || []) {
      const qty = Number(item.quantity) || 0;
      if (qty <= 0) continue;
      const pid = normalizePromotionProductId(item.product_id);
      if (pid) orderedProductIds.add(pid);
    }
  }

  return promoProductIds.filter((pid) => !orderedProductIds.has(pid));
}

/** משך מומלץ לפי טווח תאריכים (ניתן לדרוס ידנית) */
export function suggestDurationFromDates(startDate, endDate) {
  if (!startDate || !endDate) return "weekly";
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start) || Number.isNaN(end)) return "weekly";
  const diffDays = Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24));
  return diffDays > 7 ? "monthly" : "weekly";
}
